import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CollectionType } from '@enums/CollectionType';
import { IndexName } from '@enums/IndexName';
import Collection, { CherryPickCollection, CherryPickedCollectionInfo } from '@models/Collection';
import { buildOrderByStr } from '@modules/layouts/hooks/useSearchAndFilter/utils';

import { RootState } from '..';

import {
  AddCollectionItemsPayload,
  MoveCollectionItemPayload,
  RemoveCollectionItemPayload,
  SetCherryPickedCollectionDataPayload,
  SetCollectionItemsPayload,
  SetCollectionPayload,
  SetDescriptionPayload,
  SetNamePayload,
  SetOrderProvidersByIdPayload,
  SetOrderProvidersByTypePayload,
  SetQueryCollectionDataPayload,
  SetSlugPayload,
  SetTypePayload,
  UndoConvertFromCherryPickedCollectionPayload,
  UndoConvertFromQueryCollectionPayload,
} from './types';

export const getEmptyCollection = (type?: CollectionType): Collection => {
  const collectionBase: Omit<Collection, 'data' | 'type'> = {
    createdBy: '',
    createdDateTime: '',
    description: '',
    id: '',
    name: { no: '', en: '', da: '' },
    slug: '',
    tags: [],
    updatedBy: '',
    updatedDateTime: '',
  };

  switch (type) {
    case CollectionType.CHERRY_PICK:
      // return { ...collectionBase, type, data: { items: [] } };
      return {
        ...collectionBase,
        type,
        data: { items: [], indexName: IndexName.Assets, queryText: '' },
      };
    case CollectionType.QUERY:
      return { ...collectionBase, type, data: { indexName: IndexName.Assets, queryText: '' } };
    default:
      return collectionBase as Collection;
  }
};

const initialState: Collection = getEmptyCollection(CollectionType.QUERY);

// Property selectors
export const getCollectionType = (state: RootState) => state.collection.type;

export const getCollectionId = (state: RootState) => state.collection.id;

export const getCollectionData = (state: RootState) => state.collection.data;

export const getCollectionSearchQuery = createSelector([getCollectionData], data => {
  const { items, ...searchQuery } = (data as CherryPickedCollectionInfo['data']) || {};
  return searchQuery;
});

// Cherry-picked collections
export const getCollectionItems = (state: RootState) =>
  (state.collection as CherryPickCollection).data.items;

export const getCollectionItemCount = (state: RootState) =>
  (state.collection as CherryPickCollection).data.items?.length ?? 0;

// SLICE
export const collectionSlice = createSlice({
  name: 'collection',
  initialState,
  reducers: {
    setName: (state, { payload: { language, value } }: PayloadAction<SetNamePayload>) => {
      state.name[language] = value;
    },
    setCollection: (_, { payload: collection }: PayloadAction<SetCollectionPayload>) => collection,
    setSlug: (state, { payload: { slug } }: PayloadAction<SetSlugPayload>) => {
      state.slug = slug;
    },
    setDescription: (state, { payload: { description } }: PayloadAction<SetDescriptionPayload>) => {
      state.description = description;
    },

    // UPDATE COLLECTION TYPE
    setType: (state, { payload: { type } }: PayloadAction<SetTypePayload>) => {
      switch (type) {
        // case CollectionType.CHERRY_PICK:
        //   state = {
        //     ...state,
        //     data: { items: [] },
        //     type: CollectionType.CHERRY_PICK,
        //   };
        //   break;
        case CollectionType.QUERY:
          state = {
            ...state,
            data: { indexName: IndexName.Assets, queryText: '' },
            type: CollectionType.QUERY,
          };
          break;

        case CollectionType.CHERRY_PICK: {
          const { data: { items = [], indexName = IndexName.Assets, queryText = '' } = {} } =
            state as CherryPickCollection;

          state = {
            ...state,
            data: { ...state.data, items, indexName, queryText },
            type: CollectionType.CHERRY_PICK,
          };
          break;
        }
      }
    },

    setCollectionQuery: (
      state,
      {
        payload: {
          data: { orderBy, filter, ...data },
        },
      }: PayloadAction<SetQueryCollectionDataPayload>
    ) => {
      const orderByStr = buildOrderByStr(orderBy);

      state.data = { ...state.data, ...data, orderBy: orderByStr, filter };
    },

    // QUERY COLLECTIONS
    setQueryCollectionData: (
      state,
      { payload: { data } }: PayloadAction<SetQueryCollectionDataPayload>
    ) => {
      state.data = { ...state.data, ...data, orderBy: buildOrderByStr(data.orderBy) };
    },
    setOrderProvidersById: (
      state,
      { payload: id }: PayloadAction<SetOrderProvidersByIdPayload>
    ) => {
      const { data } = state;

      if (id) {
        data.orderProvidersBy = { ...data.orderProvidersBy, id };
      } else {
        delete data.orderProvidersBy?.id;
      }

      if (!Object.values(data.orderProvidersBy || {}).some(Boolean)) {
        delete data.orderProvidersBy;
      }

      state.data = data;
    },
    setOrderProvidersByType: (
      state,
      { payload: type }: PayloadAction<SetOrderProvidersByTypePayload>
    ) => {
      const { data } = state;

      if (type) {
        data.orderProvidersBy = { ...data.orderProvidersBy, type };
      } else {
        delete data.orderProvidersBy?.type;
      }

      if (!Object.values(data.orderProvidersBy || {}).some(Boolean)) {
        delete data.orderProvidersBy;
      }

      state.data = data;
    },

    // CHERRY-PICKED COLLECTIONS
    addCollectionItem: (
      state,
      { payload: { position, items } }: PayloadAction<AddCollectionItemsPayload>
    ) => {
      const arr = Array.isArray(items) ? items : [items];
      const cherryPickState = state as CherryPickCollection;

      if (position === 'back') {
        cherryPickState.data.items.push(...arr);
      } else {
        const pos = position === 'front' ? 0 : position;
        cherryPickState.data.items.splice(pos, 0, ...arr);
      }
    },
    setCherryPickedCollectionData: (
      state,
      { payload: { data } }: PayloadAction<SetCherryPickedCollectionDataPayload>
    ) => {
      state.data = data;
    },
    removeCollectionItem: (
      state,
      { payload: { item } }: PayloadAction<RemoveCollectionItemPayload>
    ) => {
      const cherryPickState = state as CherryPickCollection;
      cherryPickState.data.items = cherryPickState.data.items.filter(({ id }) => id !== item.id);
    },
    moveCollectionItem: (
      state,
      { payload: { draggedItem, item } }: PayloadAction<MoveCollectionItemPayload>
    ) => {
      const cherryPickState = state as CherryPickCollection;
      const curItems = cherryPickState.data.items;

      const newIndex = curItems.indexOf(item);
      const newItems = curItems.filter(i => i !== draggedItem);
      newItems.splice(newIndex, 0, draggedItem);

      cherryPickState.data.items = newItems;
    },
    setCollectionItems: (
      state,
      { payload: { items } }: PayloadAction<SetCollectionItemsPayload>
    ) => {
      (state as CherryPickCollection).data.items = items;
    },
    clearCollection: state => {
      (state as CherryPickCollection).data.items = [];
    },

    // UNDO CONVERSIONS
    undoConvertFromQueryCollection: (
      state,
      { payload: { data } }: PayloadAction<UndoConvertFromQueryCollectionPayload>
    ) => {
      state.type = CollectionType.QUERY;
      state.data = data;
    },
    undoConvertFromCherryPickedCollection: (
      state,
      { payload: { data } }: PayloadAction<UndoConvertFromCherryPickedCollectionPayload>
    ) => {
      state.type = CollectionType.CHERRY_PICK;
      state.data = data;
    },
  },
});

export const { actions: collectionActions } = collectionSlice;

export default collectionSlice.reducer;
